import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { firebase } from '../firebaseConfig';
import { authService } from './authApi';

const firebaseAuth = getAuth(firebase);
const MIN_ID_TOKEN_VALIDITY_MS = 2 * 60 * 1000;

const expiresAt = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      '=',
    );
    const decoded = JSON.parse(
      decodeURIComponent(
        window.atob(paddedPayload)
          .split('')
          .map((character) => `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    ) as { exp?: number };

    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isFirebaseIdTokenFresh = (token: string) => {
  const expiration = expiresAt(token);
  return expiration !== null && expiration - Date.now() > MIN_ID_TOKEN_VALIDITY_MS;
};

const getUsableIdToken = async (user: ReturnType<typeof getAuth>['currentUser']) => {
  if (!user) throw new Error('Usuário Firebase não autenticado.');

  const token = await user.getIdToken();
  // Leave enough time for queued requests and pagehide requests to reach the API.
  if (!isFirebaseIdTokenFresh(token)) {
    return user.getIdToken(true);
  }

  return token;
};

// A custom token is only needed to establish the Firebase client session. Once
// authenticated, getIdToken() uses Firebase's refresh token to renew ID tokens.
export const initializeFirebaseSession = async (accessToken: string) => {
  const { customToken } = await authService.firebaseToken(accessToken);
  const credential = await signInWithCustomToken(firebaseAuth, customToken);

  return getUsableIdToken(credential.user);
};

export const getFirebaseIdToken = async (accessToken: string, forceRefresh = false) => {
  const user = firebaseAuth.currentUser;

  if (user) {
    if (forceRefresh) return user.getIdToken(true);

    return getUsableIdToken(user);
  }

  return initializeFirebaseSession(accessToken);
};

export const signOutFirebase = () => signOut(firebaseAuth);
